import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { CustomModal } from '../../../../../../../../components/customModal'
import { Table } from '../../../../../../../../components/table'
import { AuthSelector } from '../../../../../../../../store/auth'
import { TechnologySelectors } from '../../../../../../../../store/technology'
import { AddCompanyMember } from '../AddCompanyMember'
import { RemoveMemberAlertBox } from '../RemoveMemberAlertBox'
import { ActionMenu } from './ActionMenu'
import { ProjectMemberDetail } from './ProjectMemberDetail'

const TableHeader = ['First name', 'Last name', 'Email', 'Action']

export const ProjectMembersTable = () => {
  const theme = useTheme()
  const { projectId = '' } = useParams()

  const mobileView = useMediaQuery(theme.breakpoints.down('sm'))

  const [selectedMember, setSelectedMember] = useState('')
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const [memberToDelete, setMemberToDelete] = useState({ techId: '', memberId: '', memberName: '' })

  const userRole = useSelector(AuthSelector.userCompanyRole)
  const projectMembers = useSelector(TechnologySelectors.makeTechnologyMembers)

  const filterHeader = mobileView ? TableHeader.slice(-2) : TableHeader

  const headerCells = filterHeader.map((header, index) => ({
    content: (
      <Typography key={index} color="secondary" fontWeight={700} fontSize="0.9rem">
        {header}
      </Typography>
    ),
  }))

  const handleRemoveMember = (techId: string, memberId: string, memberName: string) => {
    setMemberToDelete({ techId, memberId, memberName })
    setShowDeleteAlert(true)
  }

  const rows =
    projectMembers &&
    projectMembers.map((rowCell) => {
      const actionMenu = (
        <Box onClick={(e) => e.stopPropagation()}>
          <ActionMenu
            handleRemoveMember={() =>
              handleRemoveMember(projectId, rowCell._id, `${rowCell.firstName} ${rowCell.lastName}`)
            }
          />
        </Box>
      )

      return {
        rowProps: {
          onClick: () => {
            setSelectedMember(rowCell._id)
            setShowDetailModal(true)
          },
          sx: {
            background: 'white',
            '&:hover': { cursor: 'pointer', background: theme.palette.grey[100] },
          },
        },
        cells: [
          {
            content: (
              <Typography
                color="secondary"
                fontSize="0.875rem"
                fontWeight={theme.typography.fontWeightBold}
              >
                {rowCell.firstName}
              </Typography>
            ),
            cellProps: {
              sx: { display: { xs: 'none', sm: 'table-cell' } },
            },
          },

          {
            content: (
              <Typography
                fontSize="0.875rem"
                color="secondary.light"
                fontWeight={theme.typography.fontWeightMedium}
              >
                {rowCell.lastName}
              </Typography>
            ),
            cellProps: {
              sx: { display: { xs: 'none', sm: 'table-cell' } },
            },
          },
          {
            content: (
              <Typography
                fontSize="0.875rem"
                color="secondary.light"
                fontWeight={theme.typography.fontWeightMedium}
              >
                {rowCell.email}
              </Typography>
            ),
          },
          {
            content: actionMenu,
          },
        ],
      }
    })

  return (
    <Box sx={{ p: 2, borderRadius: '8px', backgroundColor: 'white' }}>
      <CustomModal
        open={showDetailModal}
        setOpen={setShowDetailModal}
        closeButtonAction={() => {
          setShowDetailModal(false)
          setSelectedMember('')
        }}
      >
        <ProjectMemberDetail memberId={selectedMember} />
      </CustomModal>

      <RemoveMemberAlertBox
        open={showDeleteAlert}
        setOpen={setShowDeleteAlert}
        selectedMember={memberToDelete}
      />

      {userRole === 'COMPANY_ADMIN' && <AddCompanyMember />}

      {!!rows.length && (
        <Table
          containerProps={{
            sx: {
              boxShadow: 'none',
              background: 'transparent',
            },
          }}
          head={{
            containerProps: {
              sx: { borderRadius: '8px', background: theme.palette.grey[200], width: '100%' },
            },
            rows: [{ cells: headerCells || [] }],
          }}
          body={{ rows }}
        />
      )}
    </Box>
  )
}
